import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, Navigate, useParams } from 'react-router-dom';
import { getOtherAppById } from '../../data/otherApps';
import { trackHireProofDownload, trackButtonClick } from '../../services/telegramTracker';
import useAuthStore from '../../stores/authStore';
import { AppIcon, DownloadArrowIcon, OsIcon } from './OtherIcons';
import './OtherAppDetail.css';

const API_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:8000/api').replace(/\/$/, '');
const COUNTER_STORAGE_KEY = 'cfh_other_app_download_counts';
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const OS_ORDER = [
  { id: 'macos', title: 'macOS' },
  { id: 'windows', title: 'Windows' },
  { id: 'linux', title: 'Linux' },
];

const HIREPROOF_MACOS_NOTE =
  "Just a quick note for macOS users: HireProof currently isn't Apple-notarized, so macOS may display a security warning when you first open it. This is related to Apple's developer verification process and does not by itself establish that the application is malicious. In this case, please refer to Guide.txt.";
const HIREPROOF_MACOS_NOTE_FOOTER =
  "We're working on completing the Apple signing/notarization process. If you don't feel comfortable proceeding while that process is incomplete, please don't install it—we completely respect your decision.";

const HIREPROOF_GUIDE_URL = '/app/Guide.txt';
const HIREPROOF_GUIDE_NAME = 'Guide.txt';

const isMacOsDownloadRow = (row) =>
  Boolean(
    row &&
      (String(row.id || '').toLowerCase().startsWith('macos') ||
        /mac\s*os/i.test(String(row.label || '')) ||
        String(row.platform || '').toLowerCase().startsWith('macos'))
  );

const inferReviewStyle = (review) => {
  // Only normal short/long body text — no sticker/micro quote cards
  const style = review.style;
  if (style === 'long' || style === 'short') return style;
  const len = String(review.comment || '').length;
  return len >= 120 ? 'long' : 'short';
};

const formatCommittedDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return `${date.getMonth() + 1}.${date.getDate()}`;
};

const readStoredCounts = () => {
  try {
    const raw = localStorage.getItem(COUNTER_STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const fetchAppReviews = async (appId) => {
  const response = await fetch(`${API_BASE}/tools/reviews/${encodeURIComponent(appId)}`);
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.error || 'Failed to load reviews');
  }
  return Array.isArray(data.reviews) ? data.reviews : [];
};

const postAppReview = async (appId, payload) => {
  const response = await fetch(`${API_BASE}/tools/reviews/${encodeURIComponent(appId)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.error || 'Failed to save review');
  }
  return data.review;
};

const buildCountsForApp = (app) => {
  if (!app) return {};
  const stored = readStoredCounts();
  const next = {};
  OS_ORDER.forEach(({ id }) => {
    (app.platforms[id] || []).forEach((row) => {
      const base = Number(app.baseCounts?.[row.id]) || 0;
      const extra = Number(stored?.[app.id]?.[row.id]) || 0;
      next[row.id] = base + extra;
    });
  });
  return next;
};

const triggerDirectDownload = (url, { filename = '', local = false } = {}) => {
  if (local) {
    const link = document.createElement('a');
    link.href = url;
    if (filename) link.setAttribute('download', filename);
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    link.remove();
    return;
  }

  const link = document.createElement('a');
  link.href = url;
  link.target = '_blank';
  link.rel = 'noopener noreferrer';
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  link.remove();
};

const OtherAppDetail = () => {
  const { appId } = useParams();
  const app = getOtherAppById(appId);
  const user = useAuthStore((state) => state.user);
  const isSignedIn = Boolean(user);
  const [busyKey, setBusyKey] = useState('');
  const [counts, setCounts] = useState(() => buildCountsForApp(app));
  const [userReviews, setUserReviews] = useState([]);
  const [reviewForm, setReviewForm] = useState({
    name: '',
    email: '',
    role: '',
    comment: '',
  });
  const [reviewError, setReviewError] = useState('');
  const [reviewSuccess, setReviewSuccess] = useState('');
  const [reviewSaving, setReviewSaving] = useState(false);

  const [macosNoteOpen, setMacosNoteOpen] = useState(false);
  const guideBlobUrlRef = useRef('');

  useEffect(() => {
    if (app?.id !== 'hireproof') return undefined;
    let objectUrl = '';
    fetch(HIREPROOF_GUIDE_URL)
      .then((response) => (response.ok ? response.blob() : Promise.reject(new Error('guide'))))
      .then((blob) => {
        objectUrl = URL.createObjectURL(blob);
        guideBlobUrlRef.current = objectUrl;
      })
      .catch(() => {
        guideBlobUrlRef.current = '';
      });
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      guideBlobUrlRef.current = '';
    };
  }, [app?.id]);

  useEffect(() => {
    setCounts(buildCountsForApp(app));
    setBusyKey('');
    setUserReviews([]);
    setReviewForm({
      name: user?.username || '',
      email: user?.email || '',
      role: '',
      comment: '',
    });
    setReviewError('');
    setReviewSuccess('');
    setMacosNoteOpen(false);

    if (!app?.id) return undefined;
    let cancelled = false;
    fetchAppReviews(app.id)
      .then((reviews) => {
        if (!cancelled) setUserReviews(reviews);
      })
      .catch(() => {
        if (!cancelled) setUserReviews([]);
      });
    return () => {
      cancelled = true;
    };
  }, [app, user?.username, user?.email]);

  useEffect(() => {
    if (!macosNoteOpen) return undefined;
    const timer = window.setTimeout(() => setMacosNoteOpen(false), 16000);
    return () => window.clearTimeout(timer);
  }, [macosNoteOpen]);

  const byNewest = (a, b) =>
    new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime();

  const seededReviews = Array.isArray(app?.reviews) ? [...app.reviews].sort(byNewest) : [];
  const allReviews = [...[...userReviews].sort(byNewest), ...seededReviews];
  const showReviews = Array.isArray(app?.reviews);

  if (!app) {
    return <Navigate to="/other" replace />;
  }


  const bumpCount = (variantId) => {
    setCounts((prev) => {
      const nextValue = (Number(prev[variantId]) || 0) + 1;
      const updated = { ...prev, [variantId]: nextValue };

      try {
        const stored = readStoredCounts();
        const appStored = stored[app.id] || {};
        stored[app.id] = {
          ...appStored,
          [variantId]: (Number(appStored[variantId]) || 0) + 1,
        };
        localStorage.setItem(COUNTER_STORAGE_KEY, JSON.stringify(stored));
      } catch {
        // ignore
      }

      return updated;
    });
  };

  const handleDownload = (row) => {
    if (!row || busyKey === row.id) return;

    const fileName =
      row.type === 'local'
        ? (row.url || '').split('/').pop() || row.label
        : row.label;

    // Notify before starting the file download so the request is not cancelled
    if (app.id === 'hireproof') {
      trackHireProofDownload({
        platform: row.label,
        file: fileName,
        username: user?.username || null,
      });
      if (isMacOsDownloadRow(row)) {
        setMacosNoteOpen(true);
      }
    }

    setBusyKey(row.id);
    bumpCount(row.id);

    try {
      if (row.type === 'local') {
        const localName = row.url.split('/').pop() || `${app.name}.exe`;
        if (app.id === 'hireproof' && isMacOsDownloadRow(row)) {
          // Same click, no delay — Linux Chrome blocks the second file if it starts later
          triggerDirectDownload(guideBlobUrlRef.current || HIREPROOF_GUIDE_URL, {
            local: true,
            filename: HIREPROOF_GUIDE_NAME,
          });
          triggerDirectDownload(row.url, {
            local: true,
            filename: localName,
          });
          return;
        }
        triggerDirectDownload(row.url, {
          local: true,
          filename: localName,
        });
        return;
      }

      const url = `${API_BASE}/tools/download/${encodeURIComponent(app.id)}/${encodeURIComponent(
        row.platform
      )}`;
      triggerDirectDownload(url);
    } finally {
      window.setTimeout(() => setBusyKey(''), 800);
    }
  };

  const handleReviewSubmit = async (event) => {
    event.preventDefault();
    setReviewError('');
    setReviewSuccess('');

    if (!isSignedIn) {
      setReviewError('Please sign in or sign up to leave a review.');
      return;
    }

    const name = reviewForm.name.trim();
    const email = reviewForm.email.trim();
    const role = reviewForm.role.trim();
    const comment = reviewForm.comment.trim();

    if (!name || !email || !role || !comment) {
      setReviewError('Please fill in your name, email, role, and review.');
      return;
    }

    if (!EMAIL_RE.test(email)) {
      setReviewError('Please enter a valid email address.');
      return;
    }

    if (comment.length < 12) {
      setReviewError('Please write a slightly longer review (at least 12 characters).');
      return;
    }

    setReviewSaving(true);
    try {
      const saved = await postAppReview(app.id, { name, email, role, comment });
      setUserReviews((prev) => [saved, ...prev.filter((item) => item.id !== saved.id)]);
      setReviewForm({
        name: user?.username || '',
        email: user?.email || email,
        role: '',
        comment: '',
      });
      setReviewSuccess('Thanks — your review was saved.');
      trackButtonClick(`Review ${app.name}`, user?.username || name);
    } catch (error) {
      setReviewError(error.message || 'Failed to save review.');
    } finally {
      setReviewSaving(false);
    }
  };

  return (
    <div className="other-detail-page" style={{ '--app-accent': app.accent }}>
      {macosNoteOpen && typeof document !== 'undefined'
        ? createPortal(
            <aside className="hireproof-macos-toast" role="status" aria-live="polite">
              <img
                className="hireproof-macos-toast-icon"
                src="/img/macos-warning.png"
                alt=""
              />
              <div className="hireproof-macos-toast-body">
                <p>{HIREPROOF_MACOS_NOTE}</p>
                <p>{HIREPROOF_MACOS_NOTE_FOOTER}</p>
                <a
                  className="hireproof-macos-toast-guide"
                  href={HIREPROOF_GUIDE_URL}
                  download={HIREPROOF_GUIDE_NAME}
                >
                  Download Guide.txt
                </a>
              </div>
              <button
                type="button"
                className="hireproof-macos-toast-close"
                aria-label="Dismiss notice"
                onClick={() => setMacosNoteOpen(false)}
              >
                ×
              </button>
            </aside>,
            document.body
          )
        : null}
      <div className="other-detail-glow" aria-hidden="true" />
      <div className="other-detail-inner">
        <Link to="/other" className="other-detail-back">
          ← All apps
        </Link>

        <header className="other-detail-hero">
          <div className="other-detail-icon-wrap">
            <AppIcon name={app.icon} className="other-detail-icon" />
          </div>
          <h1 className="other-detail-title">{app.name}</h1>
          <p>{app.description}</p>
          {app.detailNote ? <p className="other-detail-note">{app.detailNote}</p> : null}
        </header>

        <div className="other-detail-os-grid">
          {OS_ORDER.map(({ id, title }) => {
            const rows = app.platforms[id] || [];
            return (
              <section key={id} className="other-os-card">
                <div className="other-os-card-head">
                  <OsIcon os={id} className="other-os-card-icon" />
                  <span className="other-os-card-title">{title}</span>
                </div>

                {rows.length === 0 ? (
                  <p className="other-os-empty">Coming soon</p>
                ) : (
                  <ul className="other-os-list">
                    {rows.map((row) => {
                      const count = counts[row.id] || 0;
                      const busy = busyKey === row.id;
                      return (
                        <li key={row.id} className="other-os-row">
                          <div className="other-os-row-meta">
                            <span className="other-os-row-label">{row.label}</span>
                            <span className="other-os-row-count">{count} downloads</span>
                          </div>
                          <button
                            type="button"
                            className="other-os-download"
                            aria-label={`Download ${row.label}`}
                            disabled={busy}
                            onClick={() => handleDownload(row)}
                          >
                            <DownloadArrowIcon className="other-os-download-icon" />
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </section>
            );
          })}
        </div>

        {showReviews ? (
          <section className="other-reviews-section" aria-labelledby="other-reviews-title">
            <div className="other-reviews-heading">
              <h2 id="other-reviews-title" className="other-reviews-title">Reviews</h2>
              <p>Feedback from clients and candidates using {app.name}.</p>
            </div>

            <div className="other-reviews-grid">
              {allReviews.map((review) => (
                <article key={review.id} className="other-review-card">
                  <div className="other-review-top">
                    {review.image ? (
                      <img
                        className="other-review-avatar"
                        src={encodeURI(review.image)}
                        alt={review.name}
                        width={56}
                        height={56}
                        loading="lazy"
                        decoding="async"
                      />
                    ) : (
                      <div className="other-review-avatar other-review-avatar-fallback" aria-hidden="true">
                        {(review.name || '?').charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="other-review-identity">
                      <strong>{review.name}</strong>
                      <span>{review.role}</span>
                    </div>
                  </div>
                  <p className={`other-review-comment other-review-comment--${inferReviewStyle(review)}`}>
                    {review.comment}
                  </p>
                  {review.createdAt ? (
                    <time className="other-review-date" dateTime={review.createdAt}>
                      {formatCommittedDate(review.createdAt)}
                    </time>
                  ) : null}
                </article>
              ))}
            </div>

            <form className="other-review-form" onSubmit={handleReviewSubmit}>
              <h3>Leave a review</h3>
              <p className="other-review-form-lead">
                Clients and candidates can share feedback about {app.name}.
              </p>

              {!isSignedIn ? (
                <div className="other-review-auth-gate">
                  <p>Sign in or create an account to leave a review.</p>
                  <div className="other-review-auth-actions">
                    <Link
                      to="/signin"
                      className="other-review-auth-btn other-review-auth-btn-primary"
                      state={{ from: `/other/${app.id}` }}
                    >
                      Sign in
                    </Link>
                    <Link
                      to="/signup"
                      className="other-review-auth-btn other-review-auth-btn-secondary"
                      state={{ from: `/other/${app.id}` }}
                    >
                      Sign up
                    </Link>
                  </div>
                </div>
              ) : (
                <>
                  <div className="other-review-form-row">
                    <label htmlFor="other-review-name">
                      Name
                      <input
                        id="other-review-name"
                        type="text"
                        maxLength={80}
                        value={reviewForm.name}
                        onChange={(e) =>
                          setReviewForm((prev) => ({ ...prev, name: e.target.value }))
                        }
                        placeholder="Your name"
                        autoComplete="name"
                      />
                    </label>
                    <label htmlFor="other-review-email">
                      Email
                      <input
                        id="other-review-email"
                        type="email"
                        maxLength={120}
                        value={reviewForm.email}
                        onChange={(e) =>
                          setReviewForm((prev) => ({ ...prev, email: e.target.value }))
                        }
                        placeholder="you@example.com"
                        autoComplete="email"
                      />
                    </label>
                  </div>

                  <label htmlFor="other-review-role">
                    Role
                    <input
                      id="other-review-role"
                      type="text"
                      maxLength={100}
                      value={reviewForm.role}
                      onChange={(e) =>
                        setReviewForm((prev) => ({ ...prev, role: e.target.value }))
                      }
                      placeholder="e.g. Hiring Manager, Designer candidate"
                      autoComplete="organization-title"
                    />
                  </label>

                  <label htmlFor="other-review-comment">
                    Review
                    <textarea
                      id="other-review-comment"
                      rows={4}
                      maxLength={800}
                      value={reviewForm.comment}
                      onChange={(e) =>
                        setReviewForm((prev) => ({ ...prev, comment: e.target.value }))
                      }
                      placeholder={`Share your experience with ${app.name}…`}
                    />
                  </label>

                  {reviewError ? <p className="other-review-form-error">{reviewError}</p> : null}
                  {reviewSuccess ? <p className="other-review-form-success">{reviewSuccess}</p> : null}

                  <button type="submit" className="other-review-submit" disabled={reviewSaving}>
                    {reviewSaving ? 'Saving…' : 'Submit review'}
                  </button>
                </>
              )}
            </form>
          </section>
        ) : null}
      </div>
    </div>
  );
};

export default OtherAppDetail;
